// pages/support/KnowledgeBasePage.tsx
import { useState, useEffect } from "react";
import { Search, Eye, X, BookOpen } from "lucide-react";
import { useNavigate } from "react-router-dom";
import PageListLayout from "../../../layouts/PageListLayout";
import { KnowledgeArticle } from "../../../types/features/KnowledgeArticle";
import useAuth from "../../../hooks/features/auth/useAuth";
import useKnowledge from "../../../hooks/features/knowledge/useKnowledge";
import KnowledgeArticleModal from "../../../components/features/knowledge/KnowledgeArticleModal";


export default function KnowledgePage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedArticle, setSelectedArticle] = useState<KnowledgeArticle | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const { user } = useAuth();

  const isAdmin = user?.role === "entreprise";

  const { articles, searching, searchArticles, clearResults } = useKnowledge();

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchQuery) {
        searchArticles(searchQuery);
      } else {
        clearResults();
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery, searchArticles, clearResults]);

  const handleArticleClick = (article: KnowledgeArticle) => {
    setSelectedArticle(article);
    setModalOpen(true);
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case "Problème":
        return { label: "Problème", className: "bg-red-100 text-red-700" };
      case "Procédure Agent":
        return { label: "Procédure agent", className: "bg-blue-100 text-blue-700" };
      case "Procédure Technique":
        return { label: "Procédure technique", className: "bg-purple-100 text-purple-700" };
      case "Escalade":
        return { label: "Escalade", className: "bg-orange-100 text-orange-700" };
      case "Réponse Client":
        return { label: "Réponse client", className: "bg-green-100 text-green-700" };
      default:
        return { label: category || "Article", className: "bg-gray-100 text-gray-700" };
    }
  };

  return (
    <PageListLayout
      title="Base de connaissances"
      actions={
        isAdmin && (
          <button
            onClick={() => navigate("/entreprise/knowledge")}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700"
          >
            <BookOpen className="w-4 h-4" />
            Gérer la base
          </button>
        )
      }
    >
      <div className="p-6">
        {/* Barre de recherche */}
        <div className="max-w-2xl mx-auto">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher une solution..."
              className="w-full pl-12 pr-4 py-4 text-lg border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery("");
                  clearResults();
                }}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
          <p className="text-center text-sm text-gray-400 mt-3">Exemples : OTP, paiement bloqué, KYC, virement</p>
        </div>

        {/* Résultats de recherche */}
        {searching ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        ) : articles.length > 0 ? (
          <div className="mt-8 max-w-3xl mx-auto space-y-3">
            <p className="text-sm text-gray-500 mb-4">{articles.length} résultat(s) trouvé(s)</p>
            {articles.map((article) => {
              const category = getCategoryLabel(article.category);
              return (
                <div
                  key={article.id}
                  onClick={() => handleArticleClick(article)}
                  className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-all cursor-pointer"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${category.className}`}>{category.label}</span>
                      </div>
                      <h3 className="font-medium text-gray-900 mb-1">{article.title}</h3>
                      <p className="text-sm text-gray-500">{article.description}</p>
                      {article.keywords && article.keywords.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {article.keywords.slice(0, 3).map((keyword, idx) => (
                            <span key={idx} className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                              {keyword}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : searchQuery && !searching && articles.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Aucun résultat trouvé pour "{searchQuery}"</p>
            <p className="text-sm text-gray-400 mt-1">Essayez d'autres mots-clés</p>
          </div>
        ) : (
          <div className="mt-12 text-center">
            <div className="w-32 h-32 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
              <Search className="w-12 h-12 text-gray-300" />
            </div>
            <h3 className="mt-4 text-gray-500 font-medium">Recherchez une solution</h3>
            <p className="text-sm text-gray-400 mt-1">Tapez un mot-clé pour trouver des articles d'aide</p>
          </div>
        )}
      </div>

      {/* Modal article */}
      <KnowledgeArticleModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedArticle(null);
        }}
        article={selectedArticle}
      />
    </PageListLayout>
  );
}
